import type { AnamnesisBlock } from '@westhetics/legal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PersonRef } from '@/features/catalog/api'
import { patientKeys } from '@/features/patients/api'
import type { Category, DocumentType } from '@/features/templates/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * O fluxo de geração de documentos e a lista de documentos (docs/api/documentos.md, "Atendimentos"
 * e "Lista de documentos"; regras em docs/areas/documentos-e-assinaturas.md, RN-DOC-15 a 36). Os
 * tipos são os do contrato da função `documents`; as telas só falam com a API por estes hooks (T25).
 */

export type Flow = 'procedure' | 'standalone'
export type AppointmentState = 'open' | 'draft' | 'generated' | 'sent' | 'discarded' | 'canceled'
export type DocumentStatus =
  | 'generated'
  | 'awaiting_patient'
  | 'awaiting_clinic_signature'
  | 'signed'
  | 'not_signed'
  | 'expired'
  | 'canceled'
  | 'deleted'
export type Level = 'critical' | 'attention' | 'waiting' | 'done'
export type ImageConsent = 'yes' | 'no' | 'not_provided'
export type ResolveAt = 'step1' | 'step2' | 'patient_record' | 'clinic_profile' | 'catalog'

export interface AnamnesisRef {
  id: string
  status: string
  completedAt: string | null
  protocolNumber: string | null
  hash: string | null
  shortHash: string | null
  practitionerName: string | null
  procedureAtSendTime: string | null
}

export interface ReviewedField<T> {
  value: T
  source:
    'patient_record' | 'anamnesis' | 'catalog' | 'calculated' | 'appointment' | 'clinic_profile'
  /** Ausente no cadastro: o fluxo pede e grava na fonte (RN-DOC-17). */
  isEditable: boolean
}

export interface Guardian {
  name: string
  docType: 'cpf'
  docNumber: string
  whatsapp: string | null
}

/** A conferência da etapa 2 (RN-DOC-16, RN-DOC-17). */
export interface Review {
  patient: {
    name: ReviewedField<string>
    idDocument: ReviewedField<string | null>
    birthDate: ReviewedField<string | null>
    email: ReviewedField<string | null>
    whatsapp: ReviewedField<string | null>
    address: ReviewedField<string | null>
    guardian: ReviewedField<Guardian | null>
  }
  practitionerId: ReviewedField<string | null>
  procedureIds: ReviewedField<string[]>
  totalAmountCents: ReviewedField<number> & {
    calculatedAmountCents: number
    isManuallyEdited: boolean
  }
  paymentTerms: ReviewedField<string>
  imageConsent: ReviewedField<ImageConsent>
  /**
   * O texto livre (RN-DOC-52), com os documentos que o usam; `null` quando nenhum documento escolhido
   * usa `appointment.freeText`: aí a etapa 2 não mostra o campo.
   */
  freeText: (ReviewedField<string | null> & { templates: PersonRef[] }) | null
  procedureDate: ReviewedField<string | null>
  procedureTime: ReviewedField<string | null>
}

/** O limite do texto livre (RN-DOC-52), o mesmo da API. */
export const FREE_TEXT_MAX_LENGTH = 5000

export interface MissingItem {
  field: string
  /** Em minúsculas, para a frase "Faltam 2 campos para gerar: profissional e procedimento." */
  label: string
  resolveAt: ResolveAt
  message: string
  templates: PersonRef[]
}

export interface Warning {
  code: string
  message: string
}

export interface AppointmentProcedure {
  id: string | null
  name: string
  amountCents: number | null
  paymentTerms: string | null
}

export interface AppointmentPractitioner {
  id: string
  name: string
  profession: string
  specialty: string | null
  council: string | null
  registrationNumber: string | null
  fullRegistration: string | null
}

/** Um documento emitido (`Document` do contrato). Os campos de envio ficam nulos até Assinaturas. */
export interface Document {
  id: string
  appointmentId: string
  templateId: string | null
  templateVersionId: string | null
  version: string | null
  name: string
  type: DocumentType
  category: Category
  position: number
  hash: string | null
  shortHash: string | null
  pageCount: number | null
  status: DocumentStatus
  /** Por extenso: "Gerado, não enviado", "Aguardando paciente"… */
  label: string
  level: Level
  pendingType: string | null
  requiresIcpCertificate: boolean
  generation: number
  generatedAt: string
  generatedBy: PersonRef | null
  replacedBy: string | null
  archivedAt: string | null
  /** O documento de anamnese para quem não lê a anamnese (RN-DOC-34). */
  isRestricted: boolean
  signatureRequestId: string | null
  sentAt: string | null
  delivery: { whatsapp: string | null; email: string | null } | null
  protocolNumber: string | null
  signedAt: string | null
}

/** O atendimento do fluxo (`Appointment` do contrato). */
export interface Appointment {
  id: string
  type: Flow
  state: AppointmentState
  step: 1 | 2 | 3
  generation: number
  /** `addition`: documentos adicionados a um atendimento já enviado (RN-DOC-57). */
  origin: 'documents' | 'manual_plan' | 'legacy' | 'addition'
  /** O atendimento original do adicional; nulo nos demais. */
  parent: { id: string; generatedAt: string; protocolNumber: string | null } | null
  patient: {
    id: string
    name: string
    isMinor: boolean
    isForeign: boolean
    completeness: { percent: number; isReadyForContract: boolean }
  }
  anamnesis: AnamnesisRef | null
  isModelPatient: boolean
  templateIds: string[]
  procedureIds: string[]
  practitionerId: string | null
  totalAmountCents: number | null
  isPriceManuallyEdited: boolean
  paymentTerms: string | null
  imageConsent: ImageConsent
  /** O texto livre da etapa 2 (RN-DOC-52). */
  freeText: string | null
  procedureDate: string | null
  procedureTime: string | null
  contacts: { whatsapp: string | null; email: string | null }
  procedures: AppointmentProcedure[]
  practitioner: AppointmentPractitioner | null
  treatmentPlanId: string | null
  draftSavedAt: string | null
  revision: number
  createdAt: string
  createdBy: PersonRef | null
  generatedAt: string | null
  /** `null` até a etapa 2. */
  review: Review | null
  missingItems: { total: number; message: string | null; items: MissingItem[] }
  canGenerate: boolean
  documents: Document[]
  previous: Document[]
  warnings?: Warning[]
}

export interface IncludedOption {
  id: string
  name: string
  category: Category
  version: string
}

export interface SelectableOption extends IncludedOption {
  procedure: string | null
  linkedTo: PersonRef[]
  isSelected: boolean
}

/** `GET …/options`: inclusos e escolhíveis como a etapa 1 mostra (RN-DOC-02 a 07). */
export interface AppointmentOptions {
  anamnesis: AnamnesisRef | null
  availableAnamneses: { id: string; completedAt: string; procedureAtSendTime: string | null }[]
  isModelPatientAvailable: boolean
  included: IncludedOption[]
  selectable: SelectableOption[]
  counter: { selected: number; total: number }
}

export interface PreviewDocument {
  templateId: string
  name: string
  category: Category
  position: number
  version: string
  resolvedMarkdown: string | null
  diagnostics: {
    severity: 'error' | 'warning'
    code: string
    message: string
    line: number | null
  }[]
  isRestricted: boolean
  /** As respostas da anamnese base para o bloco `::anamnesis` (T42); nulo quando o documento não usa o bloco. */
  anamnesis?: AnamnesisBlock | null
}

export interface PreviewResult {
  documents: PreviewDocument[]
  missingItems: Appointment['missingItems']
}

export interface GenerateResult {
  appointment: Appointment
  documents: Document[]
  previous: Document[]
  warnings: Warning[]
}

export interface DocumentDetail extends Document {
  patient: { id: string; name: string }
  resolvedMarkdown: string | null
  pdf: { url: string; expiresAt: string; sizeBytes: number; fileName: string } | null
}

export interface PdfLink {
  url: string
  expiresAt: string
  sizeBytes: number
  fileName: string
  shortHash: string | null
  pageCount: number | null
}

export interface ZipLink {
  url: string
  expiresAt: string
  sizeBytes: number
  fileName: string
  documents: string[]
  omitted: { id: string; name: string; reason: string }[]
}

export interface CreateAppointmentInput {
  patientId?: string
  type: Flow
  anamnesisId?: string
  isModelPatient?: boolean
  appointmentId?: string
  /** Adicionar documentos a um atendimento já enviado (RN-DOC-57): a paciente e o tipo vêm dele. */
  parentAppointmentId?: string
}

/** O corpo do `PUT`: a etapa 1, a etapa 2, ou só data e horário (sem `step`). */
export interface UpdateAppointmentInput {
  step?: 1 | 2
  revision: number
  templateIds?: string[]
  isModelPatient?: boolean
  anamnesisId?: string | null
  practitionerId?: string | null
  procedureIds?: string[]
  totalAmountCents?: number | null
  imageConsent?: 'yes' | 'no'
  /** Vazio ou `null` limpa (RN-DOC-52). */
  freeText?: string | null
  procedureDate?: string | null
  procedureTime?: string | null
  patient?: { whatsapp?: string; email?: string }
}

// A aba Documentos da ficha (docs/api/pacientes.md, `GET /patients/:id/documents`) ------------------------

export interface PatientDocumentSummary {
  id: string
  name: string
  category: Category
  status: DocumentStatus
  label: string
  level: Level
  isRestricted: boolean
  signedAt: string | null
  protocolNumber: string | null
}

/** Um atendimento (envio) da aba Documentos. */
export interface PatientAppointment {
  appointmentId: string
  /** O atendimento original quando o bloco são documentos adicionados depois do envio (RN-DOC-57). */
  parentAppointment: { id: string; generatedAt: string } | null
  type: Flow
  state: AppointmentState
  generation: number
  procedures: string[]
  practitioner: { id: string | null; name: string } | null
  totalAmountCents: number | null
  procedureDate: string | null
  generatedAt: string
  status: DocumentStatus
  label: string
  level: Level
  procedureDoneUnsigned: boolean
  daysOpen?: number | null
  isUrgent?: boolean
  /** O envio vivo ou o último (RN-ASS-04): protocolo, canais e a entrega por canal. */
  signatureRequest: {
    id: string
    protocolNumber: string
    channels: 'whatsapp' | 'email' | 'both'
    sentAt: string
    linkExpiresAt: string
    resendCount: number
    delivery: {
      channel: 'whatsapp' | 'email'
      status:
        'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'canceled' | 'not_sent' | 'manual'
      at: string | null
      detail: string | null
      failureCode: string | null
      isPermanent: boolean
    }[]
    pendingAction: { id: string; kind: 'send' | 'resend' | 'cancel'; undoableUntil: string } | null
  } | null
  actions?: string[]
  documents: PatientDocumentSummary[]
}

/** Um rascunho do fluxo de geração (RN-DOC-35): a ficha oferece "Retomar · Descartar". */
export interface DocumentDraft {
  appointmentId: string
  type: Flow
  step: number
  templateCount: number
  createdAt: string
  draftSavedAt: string | null
  createdBy: PersonRef | null
  /** O atendimento original quando o rascunho é de documentos adicionados (RN-DOC-57). */
  parentAppointmentId: string | null
}

export interface PatientDocumentsMeta extends ListMeta {
  drafts: DocumentDraft[]
}

// Chaves e hooks ----------------------------------------------------------------------------------------

export const documentKeys = {
  all: ['documents'] as const,
  appointment: (id: string) => ['documents', 'appointment', id] as const,
  options: (id: string) => ['documents', 'options', id] as const,
  document: (appointmentId: string, documentId: string) =>
    ['documents', 'document', appointmentId, documentId] as const,
  patient: (patientId: string, page: number) => ['documents', 'patient', patientId, page] as const,
}

const base = '/documents/appointments'

/** `POST /documents/appointments`: o rascunho nasce (ou o aberto passa a `draft`), com idempotência. */
export function useCreateAppointment() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateAppointmentInput) =>
      api.post<Appointment>(base, input, { idempotencyKey: idempotency.keyFor(input) }),
    onSuccess: (appointment) => {
      idempotency.reset()
      queryClient.setQueryData(documentKeys.appointment(appointment.id), appointment)
      return queryClient.invalidateQueries({ queryKey: patientKeys.detail(appointment.patient.id) })
    },
  })
}

export function useAppointment(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: documentKeys.appointment(id ?? ''),
    queryFn: () => api.get<Appointment>(`${base}/${id}`),
    enabled: Boolean(id),
  })
}

/** `PUT …/:id`: a resposta já vem recalculada (valor, condição, pendências) e substitui a guardada. */
export function useUpdateAppointment(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateAppointmentInput) => api.put<Appointment>(`${base}/${id}`, input),
    onSuccess: (appointment) => {
      queryClient.setQueryData(documentKeys.appointment(id), appointment)
    },
  })
}

export function useDiscardAppointment() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`${base}/${id}/discard`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentKeys.all }),
  })
}

export function useAppointmentOptions(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: documentKeys.options(id ?? ''),
    queryFn: () => api.get<AppointmentOptions>(`${base}/${id}/options`),
    enabled: Boolean(id),
  })
}

/** `POST …/preview`: nada é salvo (RN-DOC-32). */
export function usePreview(id: string) {
  const api = useApi()
  return useMutation({
    mutationFn: (templateId?: string) =>
      api.post<PreviewResult>(`${base}/${id}/preview`, templateId ? { templateId } : {}),
  })
}

/**
 * `POST …/generate` ou `…/regenerate`, com `Idempotency-Key` obrigatória (RN-DOC-24): a chave
 * pertence à geração que se quer fazer (atendimento e geração atual), não ao clique.
 */
export function useGenerate(id: string, action: 'generate' | 'regenerate') {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (generation: number) =>
      api.post<GenerateResult>(
        `${base}/${id}/${action}`,
        {},
        { idempotencyKey: idempotency.keyFor({ id, action, generation }) },
      ),
    onSuccess: (result) => {
      idempotency.reset()
      queryClient.setQueryData(documentKeys.appointment(id), result.appointment)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: documentKeys.all }),
        queryClient.invalidateQueries({
          queryKey: patientKeys.detail(result.appointment.patient.id),
        }),
      ])
    },
  })
}

/** O documento para ler na tela (`?pdf=false`: sem URL e sem trilha). */
export function useDocumentDetail(appointmentId: string | null, documentId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: documentKeys.document(appointmentId ?? '', documentId ?? ''),
    queryFn: () =>
      api.get<DocumentDetail>(`${base}/${appointmentId}/documents/${documentId}`, { pdf: 'false' }),
    enabled: Boolean(appointmentId && documentId),
  })
}

/** A URL assinada do PDF (15 minutos), registrada na trilha (RN-DOC-29). */
export function usePdfLink() {
  const api = useApi()
  return useMutation({
    mutationFn: (target: { appointmentId: string; documentId: string }) =>
      api.get<PdfLink>(`${base}/${target.appointmentId}/documents/${target.documentId}/pdf`),
  })
}

/** O zip dos PDFs vivos do atendimento (RN-DOC-27, RN-DOC-28). */
export function useZipLink() {
  const api = useApi()
  return useMutation({
    mutationFn: (appointmentId: string) => api.get<ZipLink>(`${base}/${appointmentId}/zip`),
  })
}

export function usePatientDocuments(patientId: string, page: number) {
  const api = useApi()
  return useQuery({
    queryKey: documentKeys.patient(patientId, page),
    queryFn: () =>
      api.list<PatientAppointment, PatientDocumentsMeta>(`/patients/${patientId}/documents`, {
        page,
        perPage: 10,
      }),
    placeholderData: (previous) => previous,
  })
}

/** Abre a URL assinada numa aba nova; o nome do arquivo é o da API (RN-DOC-28). */
export function openSignedUrl(url: string) {
  window.open(url, '_blank', 'noopener')
}
